import { useState, ReactElement, ReactNode, FocusEventHandler } from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import classNames from 'classnames';
import get from 'lodash/get';
import FormControl, { FormControlProps } from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormHelperText from '@material-ui/core/FormHelperText';
import IconButton from '@material-ui/core/IconButton';
import EyeClosed from '~/ui/assets/images/viewClosed.svg';
import EyeOpen from '~/ui/assets/images/viewOpen.svg';
import styles from './Input.module.scss';

interface IProps {
  register: UseFormRegister<any>;
  errors: FieldErrors;
  type?: string;
  size?: FormControlProps['size'];
  name: string;
  label?: string;
  disabled?: boolean;
  placeholder?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  multiline?: boolean;
  tabIndex?: number;
  combinedInput?: boolean;
  autoFocus?: boolean;
  minRows?: number;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChangeCallback?: (value: string) => void;
  className?: string;
  helperText?: string;
}

const Input = ({
  register,
  errors,
  type = 'text',
  size = 'small',
  name,
  label = '',
  disabled = false,
  placeholder = '',
  startAdornment,
  endAdornment,
  multiline,
  tabIndex,
  combinedInput,
  autoFocus = false,
  minRows = 1,
  onBlur,
  onChangeCallback,
  className,
  helperText,
}: IProps): ReactElement => {
  const [showPassword, setShowPassword] = useState(false);

  const { ref, onChange, ...registerProps } = register(name);

  const errorText = get(errors, `${name}.message`, '');
  const isError = Boolean(errorText);

  const getInputType = () => {
    if (type !== 'password') return type;
    return showPassword ? 'text' : 'password';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e);
    if (onChangeCallback) onChangeCallback(e.target.value);
  };

  return (
    <FormControl
      variant="outlined"
      size={size}
      className={classNames(styles.inputContainer, {
        [styles.overwrittenInputContainer]: combinedInput,
      })}
    >
      {label && (
        <InputLabel shrink error={isError}>
          {label}
        </InputLabel>
      )}
      <OutlinedInput
        autoFocus={autoFocus}
        multiline={multiline}
        onKeyPress={e => {
          if (e.code === 'Minus' && type === 'number') {
            e.preventDefault();
          }
        }}
        inputProps={{
          tabIndex,
          min: 0,
        }}
        classes={{
          root: combinedInput ? styles.root : null,
          notchedOutline: combinedInput ? styles.notchedOutline : null,
        }}
        {...registerProps}
        inputRef={ref}
        className={className}
        type={getInputType()}
        notched={!!label}
        disabled={disabled}
        placeholder={placeholder}
        label={label}
        startAdornment={startAdornment}
        onBlur={onBlur}
        onChange={handleChange}
        endAdornment={
          endAdornment ||
          (type === 'password' && (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                className={styles.icon}
                onMouseDown={() => setShowPassword(true)}
                onMouseUp={() => setShowPassword(false)}
                onMouseMove={() => setShowPassword(false)}
                edge="end"
              >
                {showPassword ? (
                  <img src={EyeOpen} alt="viewClosed" />
                ) : (
                  <img src={EyeClosed} alt="viewOpen" />
                )}
              </IconButton>
            </InputAdornment>
          ))
        }
        error={isError}
        minRows={minRows}
      />
      {isError && <FormHelperText error>{errorText.toString()}</FormHelperText>}
      {!isError && helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default Input;
